import React, {
  ComponentProps,
  FC,
  useCallback,
  useMemo,
  useState,
  JSX,
} from 'react';

import { Box, Flex } from '@adsk/alloy-react-box';
import { LinkButton } from '@adsk/alloy-react-button';
import { MenuItemType } from '@adsk/alloy-react-menu';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme from '@adsk/alloy-react-theme';

import AttachmentsListEmptyState from './AttachmentsListEmptyState';
import AttachmentsListErrorState from './AttachmentsListErrorState';
import AttachmentsListViewToggle from './AttachmentsListViewToggle';
import { ATTACHMENTS_LIST_VARIANTS } from '../../consts';
import { useTranslation } from '../../i18n';
import {
  Attachment,
  AttachmentDraft,
  AttachmentListVariant,
} from '../../types';
import AttachmentItem from '../AttachmentItem/AttachmentItem';

export type AttachmentsListProps = {
  attachments?: Attachment[];
  allowDelete?: boolean;
  drafts?: AttachmentDraft[];
  loading?: boolean;
  onDeleteAttachment?: (id: string) => void;
  onDeleteDraft?: (id: string) => void;
  showLoadMore?: boolean;
  renderEmptyState?: () => JSX.Element | null;
  onLoadMore?: () => void;
  onAttachmentClick?: (attachment: Attachment) => void;
  onDraftClick?: (draft: AttachmentDraft) => void;
  onDownloadAttachment?: (attachmentId: string) => void;
  showCreatedOn?: boolean;
  showCreatedBy?: boolean;
  showSize?: boolean;
  height?: number | string;
  onRetryDraft?: (draftId: string) => void;
  showVariantToggle?: boolean;
  variant?: AttachmentListVariant;
  error?: unknown;
  onRetryLoadingAttachments?: () => void;
  onVariantToggle?: (variant: AttachmentListVariant) => void;
  getCustomItemActions?: (attachment: Attachment) => MenuItemType<string>[];
  condensed?: boolean;
  overflowContainerProps?: ComponentProps<typeof Box>;
  itemsContainerProps?: ComponentProps<typeof Box>;
};

const AttachmentsList: FC<AttachmentsListProps> = ({
  onDeleteAttachment,
  onDeleteDraft,
  attachments,
  drafts,
  loading,
  allowDelete,
  renderEmptyState = () => <AttachmentsListEmptyState />,
  showLoadMore,
  onLoadMore,
  onAttachmentClick,
  onDraftClick,
  onDownloadAttachment,
  showCreatedOn,
  showCreatedBy,
  showSize,
  height = 'auto',
  onRetryDraft,
  variant: variantFromProp = ATTACHMENTS_LIST_VARIANTS.GRID,
  showVariantToggle = true,
  error,
  onRetryLoadingAttachments,
  onVariantToggle,
  getCustomItemActions,
  condensed,
  overflowContainerProps,
  itemsContainerProps,
  ...props
}) => {
  const { t } = useTranslation();
  const [selectedVariant, setSelectedVariant] =
    useState<AttachmentListVariant>(variantFromProp);

  const hasAttachments = useMemo(
    () => Boolean(attachments && attachments.length),
    [attachments],
  );

  const variant = useMemo(() => {
    // condensed view is always list
    if (condensed) {
      return ATTACHMENTS_LIST_VARIANTS.LIST;
    }

    return selectedVariant;
  }, [selectedVariant, condensed]);

  const hasDrafts = useMemo(() => Boolean(drafts && drafts.length), [drafts]);

  // to not re-render list on variant change
  const internalItemsContainerProps = useMemo<ComponentProps<typeof Box>>(
    () =>
      variant === ATTACHMENTS_LIST_VARIANTS.GRID
        ? {
            flexDirection: 'row',
            gridTemplateColumns: 'repeat(2, minmax(100px, 1fr))',
            gridAutoRows: 'auto',
            gridColumnGap: '16px',
            gridRowGap: '16px',
            justifyContent: 'center',
            'data-testid': 'AttachmentsGridContainer',
            display: 'grid',
          }
        : {
            'data-testid': 'AttachmentsListContainer',
            flexDirection: 'column',
            display: 'flex',
          },
    [variant],
  );

  const onViewToggle = useCallback(
    (nextVariant: AttachmentListVariant) => {
      setSelectedVariant(nextVariant);

      if (onVariantToggle) {
        onVariantToggle(nextVariant);
      }
    },
    [setSelectedVariant, onVariantToggle],
  );

  const paddingProps = useMemo(
    () =>
      variant === ATTACHMENTS_LIST_VARIANTS.GRID ? { padding: '16px' } : {},
    [variant],
  );

  // initial loading
  if (loading && !hasAttachments && !showLoadMore) {
    return (
      <Flex
        data-testid="AttachmentsListLoading"
        justifyContent="center"
        padding={16}
        backgroundColor="white"
        borderBottomLeftRadius="4px"
        borderBottomRightRadius="4px"
      >
        <ProgressRing />
      </Flex>
    );
  }

  if (error) {
    return <AttachmentsListErrorState onRetry={onRetryLoadingAttachments} />;
  }

  if (!hasAttachments && !hasDrafts) {
    return renderEmptyState();
  }

  return (
    <Flex flexDirection="column" {...props}>
      {showVariantToggle && !condensed && (
        <AttachmentsListViewToggle selected={variant} onSelect={onViewToggle} />
      )}
      <Box
        data-testid="AttachmentsListOverflowContainer"
        height={height}
        overflowY="auto"
        borderTop={`1px solid ${theme.colors.charcoal200}`}
        {...paddingProps}
        {...overflowContainerProps}
      >
        <Box {...internalItemsContainerProps} {...itemsContainerProps}>
          {drafts?.map((draft) => (
            <AttachmentItem
              variant={variant}
              draft
              id={draft.attachmentId}
              key={draft.attachmentId}
              name={draft.file.name}
              uploaded={draft.uploaded}
              allowDelete={allowDelete}
              onDelete={() => {
                if (onDeleteDraft) {
                  onDeleteDraft(draft.attachmentId);
                }
              }}
              onClick={onDraftClick ? () => onDraftClick(draft) : undefined}
              uploadError={draft.error}
              createdOn={draft.createdOn}
              createdByName={draft.createdByName}
              createdBy={draft.createdBy}
              size={draft.file.size}
              showCreatedOn={showCreatedOn}
              showCreatedBy={showCreatedBy}
              showSize={showSize}
              rejected={draft.rejected}
              onRetry={() => {
                if (onRetryDraft) {
                  onRetryDraft(draft.attachmentId);
                }
              }}
            />
          ))}
          {attachments &&
            attachments.map((attachment) => (
              <AttachmentItem
                uploaded
                variant={variant}
                id={attachment.attachmentId}
                key={attachment.attachmentId}
                name={attachment.displayName}
                createdOn={attachment.createdOn}
                createdBy={attachment.createdBy}
                createdByName={attachment.createdByName}
                allowDelete={allowDelete}
                onDelete={() =>
                  onDeleteAttachment &&
                  onDeleteAttachment(attachment.attachmentId)
                }
                uploadError={attachment.error}
                onRetry={() => {
                  if (onDeleteAttachment) {
                    onDeleteAttachment(attachment.attachmentId);
                  }
                }}
                size={attachment.fileSize}
                onClick={() =>
                  onAttachmentClick && onAttachmentClick(attachment)
                }
                onDownload={() =>
                  onDownloadAttachment &&
                  onDownloadAttachment(attachment.attachmentId)
                }
                showCreatedOn={showCreatedOn}
                showCreatedBy={showCreatedBy}
                showSize={showSize}
                customItemActions={getCustomItemActions?.(attachment)}
                isDeleted={attachment.isDeleted}
                condensed={condensed}
              />
            ))}
        </Box>
        {showLoadMore && (
          <Flex
            padding="10px"
            alignItems="center"
            justifyContent="center"
            borderTop={`1px solid ${theme.colors.charcoal200}`}
          >
            <LinkButton
              disabled={loading}
              onClick={onLoadMore}
              data-testid="AttachmentsListLoadMoreButton"
            >
              {loading ? t('LOADING_DOTS') : t('LOAD_MORE')}
            </LinkButton>
          </Flex>
        )}
      </Box>
    </Flex>
  );
};

export default WithAlloySuspense(AttachmentsList);
